import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { FileText, CheckCircle, DollarSign, Clock, Shield, Users, TrendingUp, Calendar, Briefcase, AlertTriangle } from 'lucide-react';
import { SiteHeader } from '@/components/layout/SiteHeader';
import { SiteFooter } from '@/components/layout/SiteFooter';
import { LanguageSelector } from '@/components/ui/language-selector';

const TRANSLATIONS = {
  en: {
    pageTitle: "Seller & Broker Program",
    subtitle: "Comprehensive guide to our business transfer platform",
    
    // Seller Section
    sellerTitle: "Sellers",
    sellerIntro: "Sellers initially benefit from restricted access to the platform, comparable to that of a Visitor Buyer, allowing them exclusively to create their profile. The pre-admission review, for which a non-refundable fee of $300 (USD) is required, conditions the submission of the file.",
    
    cashFlowWarning: "AcquizGroup wishes to clarify that businesses that do not demonstrate positive cash flow will be automatically rejected in the pre-admission process.",
    
    postApprovalTitle: "After Approval",
    postApprovalText: "Following approval of the application by AcquizGroup, the Seller must pay the balance of the Seller package to obtain full access to the platform's features. The Seller program fees include the financial evaluation and the creation of the Confidential Information Memorandum (CIM) based on the company's SWOT analysis.",
    
    publicationTitle: "Publication & Listing",
    publicationText: "Once this step is completed, AcquizGroup proceeds to publish an anonymous overview of your business on its platform, including the submission of financial documents for pre-financing. The Seller's business is listed and submitted to Lenders anonymously for a period of one (1) year, with a quarterly system follow-up. Only verified buyers gain access to your company's confidential information, after prior signing of a Non-Disclosure Agreement (NDA).",
    
    documentsTitle: "Required Documents",
    documentsText: "Since your business valuation by Lenders is based on its key performance indicators (KPIs) and its potential for sustainability and growth, it is essential that the business owner provide the most comprehensive information possible to showcase their business. The business owner must provide, at minimum, the profit and loss (P&L) statements for the last three (3) years as well as the most recent Balance Sheet and/or Tax Reports for the last three (3) years. The owner is also required to complete the questionnaire necessary for creating the CIM (Confidential Information Memorandum) to the best of their knowledge and with the utmost honesty.",
    
    buyerContactTitle: "Buyer Contact Process",
    buyerContactText: "Following the analysis of financial data and the CIM, some Buyers will request contact. Their profile, as well as that of their partners, will be matched with the request. It is the sole responsibility of the Seller to evaluate each profile and determine the Buyers with whom they wish to make contact. Once this contact request is accepted, the Seller's calendar will be transmitted to the Buyer so they can schedule an appointment according to their availability. Therefore, the Seller must share and ensure that their calendar on the platform is up to date.",
    
    disclaimerTitle: "Platform Disclaimer",
    disclaimerText: "Although AcquizGroup is a networking platform and provides an ecosystem conducive to business acquisition and transfer, AcquizGroup cannot guarantee a successful connection or the conclusion of a transaction. Sellers have access to the consultant directory; however, any additional services requested from these professionals will be billed directly by them.",
    
    platformFee: "Platform management fees equivalent to 1% of the total transaction value will be charged by AcquizGroup.",
    
    professionalsTitle: "Qualified Professionals",
    professionalsText: "To facilitate the fluidity of business acquisitions and transfers, AcquizGroup offers a list of qualified professionals, including CPAs, notaries, business lawyers and tax advisors. However, it is expressly stipulated that the use of their services is in no way mandatory for Users.",
    
    // Broker Section
    brokerTitle: "Brokers",
    brokerIntro: "Brokers are responsible for collecting and providing the documentation necessary for listing the business they represent, including at minimum three (3) years of profit and loss (P&L) statements, the most recent available balance sheet, and/or three (3) years of tax reports. The Broker is required to submit only businesses that demonstrate positive cash flow.",
    
    brokerPreQualTitle: "Pre-Qualification",
    brokerPreQualText: "The pre-qualification review, for which a non-refundable fee of $300 (USD) is required, conditions the submission of each file. Following approval of files by AcquizGroup, the Broker-Seller must pay the balance of the Broker-Seller package to obtain full access to the platform's features.",
    
    brokerProgramTitle: "Broker-Seller Program",
    brokerProgramText: "The Broker-Seller program fees include the financial evaluation and the creation of the Confidential Information Memorandum (CIM) based on the company's SWOT analysis. Once this step is completed, AcquizGroup proceeds to publish an anonymous overview of the businesses on its platform, including the submission of financial documents for pre-financing. The Broker-Seller's businesses are listed for a period of one (1) year and submitted to Lenders anonymously, with a quarterly system follow-up. Only verified buyers gain access to the confidential information of the businesses for sale, after prior signing of a Non-Disclosure Agreement (NDA).",
    
    brokerEthicsTitle: "Ethics & Responsibilities",
    brokerEthicsText: "Brokers are also required to maintain the greatest accuracy in the information they provide as well as conduct strictly in accordance with professional ethical rules. The business owner must also complete the questionnaire necessary for creating the CIM to the best of their ability and with the utmost honesty. They must ensure that the Seller is accessible to answer questions from Buyers, Lenders or Lawyers within a reasonable time.",
    
    brokerContactTitle: "Buyer Contact Process",
    brokerContactText: "Following the analysis, some Buyers will request contact. Their profile will be matched with the request. It is the responsibility of the Seller to evaluate each profile and determine the Buyers with whom they wish to make contact. Once this contact request is accepted, the Seller's calendar will be transmitted to the Buyer so they can schedule an appointment according to their availability. Therefore, the Broker must ensure that the Seller shares and keeps their calendar up to date on the platform.",
    
    brokerDisclaimerTitle: "Platform Disclaimer",
    brokerDisclaimerText: "Although AcquizGroup is a networking platform and provides an ecosystem conducive to business transactions, AcquizGroup cannot guarantee a successful connection. Brokers have access to the consultant directory, but any additional services requested from these professionals will be billed directly by them.",
    
    finalDisclaimer: "It is clearly stipulated that AcquizGroup offers no guarantee of fruitful networking or success in completing facilitated transactions.",
    
    // Pricing
    pricingTitle: "Program Pricing",
    preAdmission: "Pre-Admission Review",
    preAdmissionDesc: "Non-refundable fee required to condition file submission",
    packageBalance: "Package Balance",
    packageBalanceDesc: "Due upon approval for full platform access",
    platformFeeTitle: "Platform Fee",
    platformFeeDesc: "1% of total transaction value",
    
    ctaTitle: "Ready to List Your Business?",
    ctaSubtitle: "Start the pre-admission process today",
    sellerRegister: "Register as Seller",
    brokerRegister: "Register as Broker",
    viewBrokerPlans: "View Broker Plans",
    contactTeam: "Contact the Team",
  },
  fr: {
    pageTitle: "Programme Vendeur & Courtier",
    subtitle: "Guide complet de notre plateforme de transfert d'entreprises",
    
    // Seller Section
    sellerTitle: "Vendeurs",
    sellerIntro: "Les Vendeurs bénéficient initialement d'un accès restreint à la plateforme, comparable à celui de l'Acheteur Visiteur, leur permettant exclusivement de créer leur profil. L'examen de pré-admission, pour lequel des frais non remboursables de 300$ (USD) sont requis, conditionne la soumission du dossier.",
    
    cashFlowWarning: "AcquizGroup tient à préciser que les entreprises qui ne démontrent pas un flux de trésorerie positif seront automatiquement rejetées dans le processus de pré-admission.",
    
    postApprovalTitle: "Après Approbation",
    postApprovalText: "Suite à l'approbation de la candidature par AcquizGroup, le Vendeur devra s'acquitter du solde du forfait Vendeur afin d'obtenir l'accès intégral aux fonctionnalités de la plateforme. Les frais du programme Vendeur comprennent l'évaluation financière et la création du Cahier d'Information Confidentiel (CIM) basé sur l'analyse SWOT de l'entreprise.",
    
    publicationTitle: "Publication & Mise en Liste",
    publicationText: "Une fois cette étape terminée, AcquizGroup procède à la publication d'une vue d'ensemble anonyme de votre entreprise sur sa plateforme, incluant la soumission des documents financiers pour le préfinancement. L'entreprise du Vendeur est listée et soumise aux Prêteurs de manière anonyme pour une durée d'un (1) an, avec une relance par le système effectuée de façon trimestrielle. Seuls les acheteurs vérifiés obtiennent l'accès aux informations confidentielles de votre entreprise, après la signature préalable d'un Accord de Non-Divulgation (NDA).",
    
    documentsTitle: "Documents Requis",
    documentsText: "Étant donné que l'évaluation de votre entreprise par les Prêteurs est basée sur ses indicateurs de performance clés (KPIs) et son potentiel de pérennité et d'accroissement, il est primordial que le propriétaire d'entreprise fournisse les informations les plus exhaustives possible pour mettre en valeur son entreprise. Le propriétaire d'entreprise devra fournir, au minimum, les états des profits et pertes (P&L) des trois (3) dernières années ainsi que le Bilan (Balance Sheet) le plus récent et/ou les Rapports d'Impôts des trois (3) dernières années. Le propriétaire est également tenu de remplir le questionnaire nécessaire à la création du CIM (Cahier d'Information Confidentiel) au meilleur de sa connaissance et avec la plus grande honnêteté.",
    
    buyerContactTitle: "Processus de Contact Acheteur",
    buyerContactText: "Suite à l'analyse des données financières et du CIM, certains Acheteurs feront une demande de prise de contact. Leur profil, ainsi que celui de leurs partenaires, sera jumelé à la demande. Il est de la responsabilité exclusive du Vendeur d'évaluer chaque profil et de déterminer les Acheteurs avec qui il souhaite entrer en contact. Une fois cette demande de contact acceptée, le calendrier du Vendeur sera transmis à l'Acheteur afin qu'il puisse planifier un rendez-vous selon ses disponibilités. Par conséquent, le Vendeur doit partager et s'assurer que son calendrier sur la plateforme est à jour.",
    
    disclaimerTitle: "Avis de Non-Responsabilité",
    disclaimerText: "Bien qu'AcquizGroup soit une plateforme de mise en relation et procure un écosystème propice à l'acquisition et aux transferts d'entreprise, AcquizGroup ne peut garantir une mise en relation réussie ou la conclusion d'une transaction. Les Vendeurs bénéficient d'un accès au répertoire des consultants; cependant, tout service supplémentaire sollicité auprès de ces professionnels sera facturé directement par ces derniers.",
    
    platformFee: "Des frais de gestion de plate-forme équivalents à 1% de la valeur totale de la transaction seront facturés par AcquizGroup.",
    
    professionalsTitle: "Professionnels Qualifiés",
    professionalsText: "Afin de faciliter la fluidité des acquisitions et des transferts d'entreprises, AcquizGroup propose une liste de professionnels qualifiés, incluant des CPA, notaires, avocats en droit des affaires et conseillers fiscaux (Tax Advisors). Il est toutefois expressément stipulé que l'utilisation de leurs services n'est en aucun cas obligatoire pour les Utilisateurs.",
    
    // Broker Section
    brokerTitle: "Courtiers (Brokers)",
    brokerIntro: "Les Courtiers ont la responsabilité de recueillir et de fournir la documentation nécessaire à la mise en vente de l'entreprise qu'ils représentent, comprenant au minimum trois (3) années d'états des profits et pertes (P&L), le bilan le plus récent disponible, et/ou trois (3) années de rapports de taxes. Le Courtier est tenu de soumettre uniquement des entreprises qui démontrent un flux de trésorerie positif.",
    
    brokerPreQualTitle: "Pré-Qualification",
    brokerPreQualText: "L'examen de pré-qualification, pour lequel des frais non remboursables de 300$ (USD) sont requis, conditionne la soumission de chaque dossier. Suite à l'approbation des dossiers par AcquizGroup, le Broker-vendeur devra s'acquitter du solde du forfait Broker-vendeur, afin d'obtenir l'accès intégral aux fonctionnalités de la plateforme.",
    
    brokerProgramTitle: "Programme Broker-Vendeur",
    brokerProgramText: "Les frais du programme Broker-Vendeur comprennent l'évaluation financière et la création du Cahier d'Information Confidentiel (CIM) basé sur l'analyse SWOT de l'entreprise. Une fois cette étape terminée, AcquizGroup procède à la publication d'une vue d'ensemble anonyme des entreprises sur sa plateforme, incluant la soumission des documents financiers pour le préfinancement. Les entreprises du Broker-Vendeur sont listées pour une durée d'un (1) an et soumises aux Prêteurs de manière anonyme, avec une relance par le système effectuée de façon trimestrielle. Seuls les acheteurs vérifiés obtiennent l'accès aux informations confidentielles des entreprises mises en vente, après la signature préalable d'un Accord de Non-Divulgation (NDA).",
    
    brokerEthicsTitle: "Éthique & Responsabilités",
    brokerEthicsText: "Les Courtiers sont également tenus de respecter la plus grande exactitude dans les informations qu'ils fournissent ainsi qu'une conduite strictement conforme aux règles d'éthique professionnelles. Le propriétaire de l'entreprise devra également remplir le questionnaire nécessaire à la création du CIM au mieux de ses capacités et avec la plus grande honnêteté. Ils doivent s'assurer que le Vendeur est accessible pour répondre aux questions des Acheteurs, Prêteurs ou Avocats dans un délai raisonnable.",
    
    brokerContactTitle: "Processus de Contact Acheteur",
    brokerContactText: "Suite à l'analyse, certains Acheteurs feront une demande de prise de contact. Leur profil sera jumelé à la demande. Il est de la responsabilité du Vendeur d'évaluer chaque profil et de déterminer les Acheteurs avec lesquels il souhaite entrer en contact. Une fois cette demande de contact acceptée, le calendrier du Vendeur sera transmis à l'Acheteur afin qu'il puisse planifier un rendez-vous selon ses disponibilités. Par conséquent, le Courtier doit s'assurer que le Vendeur partage et maintient à jour son calendrier sur la plateforme.",
    
    brokerDisclaimerTitle: "Avis de Non-Responsabilité",
    brokerDisclaimerText: "Bien qu'AcquizGroup soit une plateforme de mise en relation et procure un écosystème propice aux transactions d'affaires, AcquizGroup ne peut garantir une mise en relation réussie. Les Courtiers bénéficient d'un accès au répertoire des consultants, mais tout service supplémentaire demandé auprès de ces professionnels sera facturé directement par ces derniers.",
    
    finalDisclaimer: "Il est clairement stipulé qu'AcquizGroup n'offre aucune garantie de mise en relation fructueuse ou de succès dans l'aboutissement des transactions facilitées.",
    
    // Pricing
    pricingTitle: "Tarification du Programme",
    preAdmission: "Examen de Pré-Admission",
    preAdmissionDesc: "Frais non remboursables requis pour la soumission du dossier",
    packageBalance: "Solde du Forfait",
    packageBalanceDesc: "Dû après approbation pour l'accès complet à la plateforme",
    platformFeeTitle: "Frais de Plateforme",
    platformFeeDesc: "1% de la valeur totale de la transaction",
    
    ctaTitle: "Prêt à lister votre entreprise?",
    ctaSubtitle: "Commencez le processus de pré-admission aujourd'hui",
    sellerRegister: "S'inscrire comme Vendeur",
    brokerRegister: "S'inscrire comme Courtier",
    viewBrokerPlans: "Voir les Plans Courtier",
    contactTeam: "Contacter l'équipe",
  }
};

export default function SellerPlan() {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const t = TRANSLATIONS[language as keyof typeof TRANSLATIONS] || TRANSLATIONS.en;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 font-sans">
      <SiteHeader />

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-[#1D314D] to-[#2E4663] py-20">
        <div className="max-w-6xl mx-auto px-4 text-center">
          {/* Language Toggle */}
          <div className="flex justify-center mb-6">
            <LanguageSelector />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            {t.pageTitle}
          </h1>
          <p className="text-xl text-white/80 max-w-2xl mx-auto">
            {t.subtitle}
          </p>
        </div>
      </section>

      {/* Pricing Overview */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-[#1D314D] text-center mb-12">
            {t.pricingTitle}
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {/* Pre-Admission */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-slate-200 text-center">
              <div className="w-16 h-16 bg-[#CEAB65]/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <FileText className="w-8 h-8 text-[#CEAB65]" />
              </div>
              <h3 className="text-xl font-bold text-[#1D314D] mb-2">{t.preAdmission}</h3>
              <p className="text-4xl font-bold text-[#CEAB65] mb-4">$300 <span className="text-lg text-slate-500">USD</span></p>
              <p className="text-slate-600 text-sm">{t.preAdmissionDesc}</p>
            </div>

            {/* Package Balance */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border-2 border-[#CEAB65] text-center relative">
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-[#CEAB65] text-white px-4 py-1 rounded-full text-sm font-medium">
                {language === 'fr' ? 'Après approbation' : 'After Approval'}
              </div>
              <div className="w-16 h-16 bg-[#1D314D]/10 rounded-full flex items-center justify-center mx-auto mb-6 mt-2">
                <DollarSign className="w-8 h-8 text-[#1D314D]" />
              </div>
              <h3 className="text-xl font-bold text-[#1D314D] mb-2">{t.packageBalance}</h3>
              <p className="text-4xl font-bold text-[#1D314D] mb-4">$2,449 <span className="text-lg text-slate-500">USD</span></p>
              <p className="text-slate-600 text-sm">{t.packageBalanceDesc}</p>
            </div>

            {/* Platform Fee */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-slate-200 text-center">
              <div className="w-16 h-16 bg-[#CEAB65]/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <TrendingUp className="w-8 h-8 text-[#CEAB65]" />
              </div>
              <h3 className="text-xl font-bold text-[#1D314D] mb-2">{t.platformFeeTitle}</h3>
              <p className="text-4xl font-bold text-[#CEAB65] mb-4">1%</p>
              <p className="text-slate-600 text-sm">{t.platformFeeDesc}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Seller Section */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-12 h-12 bg-[#1D314D] rounded-full flex items-center justify-center">
              <Briefcase className="w-6 h-6 text-white" />
            </div>
            <h2 className="text-3xl font-bold text-[#1D314D]">{t.sellerTitle}</h2>
          </div>

          <div className="space-y-8">
            <p className="text-slate-700 leading-relaxed">{t.sellerIntro}</p>

            {/* Warning Box */}
            <div className="bg-amber-50 border-l-4 border-amber-500 p-6 rounded-r-lg">
              <div className="flex items-start gap-4">
                <AlertTriangle className="w-6 h-6 text-amber-500 flex-shrink-0 mt-1" />
                <p className="text-amber-800 font-medium">{t.cashFlowWarning}</p>
              </div>
            </div>

            {/* Post Approval */}
            <div className="bg-slate-50 rounded-xl p-6">
              <h3 className="text-xl font-bold text-[#1D314D] mb-3 flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-[#CEAB65]" />
                {t.postApprovalTitle}
              </h3>
              <p className="text-slate-700 leading-relaxed">{t.postApprovalText}</p>
            </div>

            {/* Publication */}
            <div className="bg-slate-50 rounded-xl p-6">
              <h3 className="text-xl font-bold text-[#1D314D] mb-3 flex items-center gap-2">
                <Clock className="w-5 h-5 text-[#CEAB65]" />
                {t.publicationTitle}
              </h3>
              <p className="text-slate-700 leading-relaxed">{t.publicationText}</p>
            </div>

            {/* Documents */}
            <div className="bg-slate-50 rounded-xl p-6">
              <h3 className="text-xl font-bold text-[#1D314D] mb-3 flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#CEAB65]" />
                {t.documentsTitle}
              </h3>
              <p className="text-slate-700 leading-relaxed">{t.documentsText}</p>
            </div>

            {/* Buyer Contact */}
            <div className="bg-slate-50 rounded-xl p-6">
              <h3 className="text-xl font-bold text-[#1D314D] mb-3 flex items-center gap-2">
                <Users className="w-5 h-5 text-[#CEAB65]" />
                {t.buyerContactTitle}
              </h3>
              <p className="text-slate-700 leading-relaxed">{t.buyerContactText}</p>
            </div>

            {/* Disclaimer */}
            <div className="bg-blue-50 rounded-xl p-6">
              <h3 className="text-xl font-bold text-[#1D314D] mb-3 flex items-center gap-2">
                <Shield className="w-5 h-5 text-blue-500" />
                {t.disclaimerTitle}
              </h3>
              <p className="text-slate-700 leading-relaxed mb-4">{t.disclaimerText}</p>
              <p className="text-[#1D314D] font-semibold">{t.platformFee}</p>
            </div>

            {/* Professionals */}
            <div className="bg-slate-50 rounded-xl p-6">
              <h3 className="text-xl font-bold text-[#1D314D] mb-3">{t.professionalsTitle}</h3>
              <p className="text-slate-700 leading-relaxed">{t.professionalsText}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Broker Section */}
      <section className="py-16 px-4 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-12 h-12 bg-[#CEAB65] rounded-full flex items-center justify-center">
              <Briefcase className="w-6 h-6 text-white" />
            </div>
            <h2 className="text-3xl font-bold text-[#1D314D]">{t.brokerTitle}</h2>
          </div>

          <div className="space-y-8">
            <p className="text-slate-700 leading-relaxed">{t.brokerIntro}</p>

            {/* Pre-Qualification */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
              <h3 className="text-xl font-bold text-[#1D314D] mb-3 flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#CEAB65]" />
                {t.brokerPreQualTitle}
              </h3>
              <p className="text-slate-700 leading-relaxed">{t.brokerPreQualText}</p>
            </div>

            {/* Broker Program */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
              <h3 className="text-xl font-bold text-[#1D314D] mb-3 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-[#CEAB65]" />
                {t.brokerProgramTitle}
              </h3>
              <p className="text-slate-700 leading-relaxed">{t.brokerProgramText}</p>
            </div>

            {/* Ethics */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
              <h3 className="text-xl font-bold text-[#1D314D] mb-3 flex items-center gap-2">
                <Shield className="w-5 h-5 text-[#CEAB65]" />
                {t.brokerEthicsTitle}
              </h3>
              <p className="text-slate-700 leading-relaxed">{t.brokerEthicsText}</p>
            </div>

            {/* Contact Process */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
              <h3 className="text-xl font-bold text-[#1D314D] mb-3 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-[#CEAB65]" />
                {t.brokerContactTitle}
              </h3>
              <p className="text-slate-700 leading-relaxed">{t.brokerContactText}</p>
            </div>

            {/* Disclaimer */}
            <div className="bg-blue-50 rounded-xl p-6">
              <h3 className="text-xl font-bold text-[#1D314D] mb-3 flex items-center gap-2">
                <Shield className="w-5 h-5 text-blue-500" />
                {t.brokerDisclaimerTitle}
              </h3>
              <p className="text-slate-700 leading-relaxed mb-4">{t.brokerDisclaimerText}</p>
              <p className="text-[#1D314D] font-semibold">{t.platformFee}</p>
            </div>

            {/* Final Disclaimer */}
            <div className="bg-amber-50 border-l-4 border-amber-500 p-6 rounded-r-lg">
              <p className="text-amber-800 font-medium">{t.finalDisclaimer}</p>
            </div>

            {/* Professionals */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
              <h3 className="text-xl font-bold text-[#1D314D] mb-3">{t.professionalsTitle}</h3>
              <p className="text-slate-700 leading-relaxed">{t.professionalsText}</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-[#1D314D] to-[#2E4663]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            {t.ctaTitle}
          </h2>
          <p className="text-xl text-white/80 mb-8">
            {t.ctaSubtitle}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center flex-wrap">
            <button
              onClick={() => navigate('/register/sellers')}
              className="bg-[#CEAB65] text-[#1D314D] px-8 py-4 rounded-lg font-semibold hover:bg-[#CEAB65]/90 transition-colors"
            >
              {t.sellerRegister}
            </button>
            <button
              onClick={() => navigate('/register/brokers')}
              className="bg-white text-[#1D314D] px-8 py-4 rounded-lg font-semibold hover:bg-white/90 transition-colors"
            >
              {t.brokerRegister}
            </button>
            <button
              onClick={() => navigate('/landing/brokers')}
              className="bg-[#1D314D] border-2 border-[#CEAB65] text-[#CEAB65] px-8 py-4 rounded-lg font-semibold hover:bg-[#CEAB65]/10 transition-colors"
            >
              {t.viewBrokerPlans}
            </button>
            <button
              onClick={() => navigate('/contact')}
              className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white/10 transition-colors"
            >
              {t.contactTeam}
            </button>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
